import clsx from 'clsx';
import React, { useContext, useEffect, useState } from 'react';
import { SelectedAccountContext } from 'src/app/app/(user-pages)/(accounts)/account/[accountId]/AccountClientLayout';
import { InfoCircle } from 'src/app/app/icons/InfoCircle';
import { useUpdateAccountMutation } from 'src/utils/react-query-hooks';
const Tooltip = React.lazy(() => import('@/components/presentational/tailwind/TooltipWrapper'));

export const AVAILABLE_COUNTRIES = [
  { label: 'All countries', emoji: '🌎', checked: true, all: true },
  // Most popular countries
  { label: 'United States', emoji: '🇺🇸', checked: false },
  { label: 'United Kingdom', emoji: '🇬🇧', checked: false },
  { label: 'Canada', emoji: '🇨🇦', checked: false },
  { label: 'Australia', emoji: '🇦🇺', checked: false },
  // European countries
  { label: 'Spain', emoji: '🇪🇸', checked: false },
  { label: 'Italy', emoji: '🇮🇹', checked: false },
  { label: 'Germany', emoji: '🇩🇪', checked: false },
  { label: 'France', emoji: '🇫🇷', checked: false },
  { label: 'Netherlands', emoji: '🇳🇱', checked: false },
  { label: 'Sweden', emoji: '🇸🇪', checked: false },
  { label: 'Norway', emoji: '🇳🇴', checked: false },
  { label: 'Slovakia', emoji: '🇸🇰', checked: false },
  { label: 'Greece', emoji: '🇬🇷', checked: false },
  { label: 'Switzerland', emoji: '🇨🇭', checked: false },
  { label: 'Turkey', emoji: '🇹🇷', checked: false },
  { label: 'Poland', emoji: '🇵🇱', checked: false },
  { label: 'Belgium', emoji: '🇧🇪', checked: false },
  { label: 'Romania', emoji: '🇷🇴', checked: false },
  { label: 'Czech Republic', emoji: '🇨🇿', checked: false },
  { label: 'Austria', emoji: '🇦🇹', checked: false },
  { label: 'Portugal', emoji: '🇵🇹', checked: false },
  { label: 'Hungary', emoji: '🇭🇺', checked: false },
  { label: 'Ireland', emoji: '🇮🇪', checked: false },
  // Latin American countries
  { label: 'Brazil', emoji: '🇧🇷', checked: false },
  { label: 'Mexico', emoji: '🇲🇽', checked: false },
  { label: 'Argentina', emoji: '🇦🇷', checked: false },
  { label: 'Colombia', emoji: '🇨🇴', checked: false },
  // Asian countries
  { label: 'Philippines', emoji: '🇵🇭', checked: false },
  { label: 'Singapore', emoji: '🇸🇬', checked: false },
  { label: 'United Arab Emirates', emoji: '🇦🇪', checked: false },
  { label: 'Indonesia', emoji: '🇮🇩', checked: false },
  { label: 'India', emoji: '🇮🇳', checked: false },
  // Other countries
  { label: 'New Zealand', emoji: '🇳🇿', checked: false },
];

export const IGCountriesOptions = React.memo(() => {

  const { account, setAccountContext } = useContext(SelectedAccountContext);
  const { mutate: mutateAccount, isLoading: isLoadingAccountUpdate } = useUpdateAccountMutation({ accountId: account?.id, hideToast: true });
  const [selectedCountries, setSelectedCountries] = useState<string[]>(account.targeting_options['locations']);
  useEffect(() => {
    setSelectedCountries(account.targeting_options['locations']);
  }, [account]);

  const [checkboxes, setCheckboxes] = useState(() => {
    const updatedCountries = AVAILABLE_COUNTRIES.map(country => ({
      ...country,
      checked: selectedCountries && Array.isArray(selectedCountries) && selectedCountries.includes(country.label.toLowerCase()),
    }));
    return updatedCountries;
  });

  const handleCheckboxChange = (index) => {
    const updatedCheckboxes = [...checkboxes];
    if (updatedCheckboxes[index].all) { // If "All" option is checked

      // If "All" option is checked and no other option is checked, do nothing
      if (updatedCheckboxes[index].checked === true && updatedCheckboxes.every(checkbox => checkbox.checked === false || checkbox.all === true))
        return
      updatedCheckboxes.forEach((checkbox, i) => { // Uncheck all other options
        if (!checkbox.all)
          checkbox.checked = false;
      });
      // Toggle "All" option
      updatedCheckboxes[index].checked = !updatedCheckboxes[index].checked;

    } else { // If any other option is checked
      updatedCheckboxes[index].checked = !updatedCheckboxes[index].checked;
      if (!updatedCheckboxes.some(checkbox => checkbox.checked && !checkbox.all))
        // Check "All" option if no checkbox is selected
        updatedCheckboxes[0].checked = true;
      else
        updatedCheckboxes[0].checked = false; // Uncheck "All" option
    }
    setCheckboxes(updatedCheckboxes);

    const activeCountries = updatedCheckboxes.filter(checkbox => checkbox.checked).map(checkbox => checkbox.label.toLocaleLowerCase())

    console.log(activeCountries)
    mutateAccount({
      targeting_options: {
        ...account.targeting_options,
        locations: activeCountries,
      }
    }, {
      onSuccess: (data) => {
        if (data)
          setAccountContext(data);
      }
    });
  };

  return (
    <>
      <React.Suspense fallback={<CardHeaderTitle />}>
        <Tooltip className="absolute left-10 ml-1 max-w-[300px] shadow-xl"
          content={"Choose your target audience's location. Keesy uses AI to determine user locations from their bios and posts."}
          placement="auto"
          animation="duration-300">
          <CardHeaderTitle />
        </Tooltip>
      </React.Suspense>

      <div className="flex flex-wrap justify-start gap-2 mt-1 mb-2">
        {checkboxes.map((checkbox, index) => (
          <button
            key={index}
            onClick={() => handleCheckboxChange(index)}
            className={clsx(
              "relative inline-flex items-center justify-center p-0.5 my-1 mx-0.5 text-sm font-medium cursor-pointer rounded-[10px] transition-all duration-300 overflow-hidden",
              checkbox.checked
                ? 'text-neutral-700 animate-sway-instantly'
                : 'text-neutral-700 hover:text-neutral-900 bg-white hover:bg-neutral-100 transition-all',
            )}
            style={checkbox.checked ? {} : { boxShadow: '0px 0px 0px 1px rgba(225, 226, 228, 0.56)' }}
          >
            {checkbox.checked && (
              <span className="absolute animate-fade-in duration-300 inset-0 bg-gradient-to-r from-[#7549F4] to-[#FCA6E9] rounded-[10px]"></span>
            )}
            <span className="relative px-3 py-2 overflow-hidden whitespace-nowrap rounded-[8px]"
              style={checkbox.checked ? { background: 'linear-gradient(135deg, #f6f3fd 0%, #fdf7fd 100%), linear-gradient(180deg, rgb(255, 255, 255, 0.16) 0%, rgb(255, 255, 255, 0) 100%)' } : {}}>
              <span className='mr-2'>{checkbox.emoji}</span>
              {checkbox.label}
            </span>
          </button>
        ))}
      </div>
    </>
  );
});

const CardHeaderTitle = () => (
  <div className="flex items-center gap-2 mb-4">
    <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <g clip-path="url(#clip0_2513_3134)">
        <path d="M4.1665 11.9053C2.6237 12.5859 1.6665 13.5343 1.6665 14.5833C1.6665 16.6544 5.39746 18.3333 9.99984 18.3333C14.6022 18.3333 18.3332 16.6544 18.3332 14.5833C18.3332 13.5343 17.376 12.5859 15.8332 11.9053M14.9998 6.66663C14.9998 10.053 11.2498 11.6666 9.99984 14.1666C8.74984 11.6666 4.99984 10.053 4.99984 6.66663C4.99984 3.9052 7.23841 1.66663 9.99984 1.66663C12.7613 1.66663 14.9998 3.9052 14.9998 6.66663ZM10.8332 6.66663C10.8332 7.12686 10.4601 7.49996 9.99984 7.49996C9.5396 7.49996 9.1665 7.12686 9.1665 6.66663C9.1665 6.20639 9.5396 5.83329 9.99984 5.83329C10.4601 5.83329 10.8332 6.20639 10.8332 6.66663Z" stroke="url(#paint0_linear_2513_3134)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4.1665 11.9053C2.6237 12.5859 1.6665 13.5343 1.6665 14.5833C1.6665 16.6544 5.39746 18.3333 9.99984 18.3333C14.6022 18.3333 18.3332 16.6544 18.3332 14.5833C18.3332 13.5343 17.376 12.5859 15.8332 11.9053M14.9998 6.66663C14.9998 10.053 11.2498 11.6666 9.99984 14.1666C8.74984 11.6666 4.99984 10.053 4.99984 6.66663C4.99984 3.9052 7.23841 1.66663 9.99984 1.66663C12.7613 1.66663 14.9998 3.9052 14.9998 6.66663ZM10.8332 6.66663C10.8332 7.12686 10.4601 7.49996 9.99984 7.49996C9.5396 7.49996 9.1665 7.12686 9.1665 6.66663C9.1665 6.20639 9.5396 5.83329 9.99984 5.83329C10.4601 5.83329 10.8332 6.20639 10.8332 6.66663Z" stroke="url(#paint1_linear_2513_3134)" strokeOpacity="0.16" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <defs>
        <linearGradient id="paint0_linear_2513_3134" x1="-5.94654" y1="10.2401" x2="10.2399" y2="26.4265" gradientUnits="userSpaceOnUse">
          <stop stopColor="#7549F4" />
          <stop offset="1" stopColor="#FCA6E9" />
        </linearGradient>
        <linearGradient id="paint1_linear_2513_3134" x1="9.99984" y1="1.66663" x2="9.99984" y2="18.3333" gradientUnits="userSpaceOnUse">
          <stop stopColor="white" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </linearGradient>
        <clipPath id="clip0_2513_3134">
          <rect width="20" height="20" fill="white" />
        </clipPath>
      </defs>
    </svg>
    <div className='inline-flex flex-row items-center'>
      <div className="inline-flex items-center text-base font-medium leading-normal text-zinc-900">
        Follower Locations
        <InfoCircle className="w-5 h-5 ml-2 text-gray-400 place-self-center ignore" />
      </div>
    </div>
  </div>
)